import _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { ReflexElement, ReflexSplitter } from 'react-reflex';
import { renderIt } from '@/formbuilder/FormElementRenderer';
import { SizedReflexContainer } from '@/formbuilder/formElements/SizedReflexContainer';
import {
  resolveFlexLayoutPaneFlex,
  resolveSavedPaneFlex,
} from '@/formbuilder/formElements/flexLayoutSplitter.utilities';
import type { FormElement } from '@/formbuilder/formBuilder.constants';
import type { FlexLayoutSplitterProps } from '@/formbuilder/formElements/FlexLayoutSplitter';
import type { FlexLayoutPaneFlex } from '@/formbuilder/formElements/flexLayoutSplitter.utilities';

export const isFlexLayoutSplitter = (definition: FormElement | undefined): definition is FlexLayoutSplitterProps =>
  definition?.component === 'FlexLayoutSplitter';

const isSamePaneFlex = (left: FlexLayoutPaneFlex | null, right: FlexLayoutPaneFlex | null): boolean =>
  left?.upperFlex === right?.upperFlex && left?.lowerFlex === right?.lowerFlex;

type FlexLayoutSplitPanesProps = {
  upper: FormElement[];
  lower: FormElement[];
  upperPaneFlex?: number;
  onUpperPaneFlexChange?: (flex: number) => void;
};

/**
 * Horizontal reflex split used by FormBuilder when a FlexLayoutSplitter is in the form.
 * Drag updates are kept in a ref and applied as flex props only on window resize so
 * react-reflex does not recompute the layout and grow the parent during the drag.
 */
export const FlexLayoutSplitPanes = ({
  upper,
  lower,
  upperPaneFlex,
  onUpperPaneFlexChange,
}: FlexLayoutSplitPanesProps) => {
  // Snapshot on mount so persisting a drag does not recompute flex from the new
  // preference and resize the parent container.
  const [savedUpperPaneFlex] = useState(() => upperPaneFlex);
  const pendingPaneFlexRef = useRef<FlexLayoutPaneFlex | null>(null);
  const [paneFlex, setPaneFlex] = useState<FlexLayoutPaneFlex | null>(null);
  const appliedPaneFlexRef = useRef<FlexLayoutPaneFlex | null>(null);
  const onUpperPaneFlexChangeRef = useRef(onUpperPaneFlexChange);
  onUpperPaneFlexChangeRef.current = onUpperPaneFlexChange;

  useEffect(() => {
    const applyPendingPaneFlexBeforeReflexResize = () => {
      const pending = pendingPaneFlexRef.current;
      if (!pending || isSamePaneFlex(pending, appliedPaneFlexRef.current)) {
        return;
      }
      // Capture phase + flushSync so the new flex is on the children before
      // react-reflex's windowResizeAware handler recomputes from props.
      flushSync(() => {
        setPaneFlex(pending);
      });
      appliedPaneFlexRef.current = pending;
    };
    window.addEventListener('resize', applyPendingPaneFlexBeforeReflexResize, true);
    return () => window.removeEventListener('resize', applyPendingPaneFlexBeforeReflexResize, true);
  }, []);

  return (
    <SizedReflexContainer orientation="horizontal">
      {(hostSize) => {
        const { upperFlex, lowerFlex } = paneFlex ?? resolveSavedPaneFlex(savedUpperPaneFlex);
        return [
          <ReflexElement
            key="upper-pane"
            {...(upperFlex !== undefined ? { flex: upperFlex } : {})}
            onStopResize={({ domElement }) => {
              if (!(domElement instanceof HTMLElement)) {
                return;
              }
              const parent = domElement.parentElement;
              const containerHeight = parent instanceof HTMLElement ? parent.offsetHeight : hostSize.height;
              const nextPaneFlex = resolveFlexLayoutPaneFlex(domElement.offsetHeight, containerHeight);
              if (nextPaneFlex.upperFlex === undefined) {
                return;
              }
              onUpperPaneFlexChangeRef.current?.(nextPaneFlex.upperFlex);
              pendingPaneFlexRef.current = nextPaneFlex;
            }}>
            {_.map(upper, (def) => renderIt(def))}
          </ReflexElement>,
          <ReflexSplitter
            key="splitter"
            className="tw:border tw:border-sq-formula-border"
            data-testid="splitter"
            onResize={() => window?.getSelection()?.removeAllRanges()}
          />,
          <ReflexElement key="lower-pane" className="tw:flex" {...(lowerFlex !== undefined ? { flex: lowerFlex } : {})}>
            {_.map(lower, (def) => renderIt(def))}
          </ReflexElement>,
        ];
      }}
    </SizedReflexContainer>
  );
};
